import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Mail, Phone, MapPin } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { COMPANY } from "@/data/site";
import MapsEmbed from "./MapsEmbed";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = ["Gebäudereinigung", "Industriereinigung", "Bauendreinigung", "Sonstiges"];

export default function Kontakt() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "Gebäudereinigung",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error("Bitte geben Sie Ihren Namen an.");
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return toast.error("Bitte gültige E-Mail-Adresse angeben.");
    if (form.message.trim().length < 5) return toast.error("Bitte hinterlassen Sie eine kurze Nachricht.");

    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      if (data?.status === "ok" || data?.status === "stored") {
        toast.success("Vielen Dank! Wir melden uns innerhalb von 24 Stunden.");
        setDone(true);
        setForm({ name: "", email: "", phone: "", service: "Gebäudereinigung", message: "" });
      } else {
        toast.error("Etwas ist schiefgelaufen. Bitte erneut versuchen.");
      }
    } catch (err) {
      toast.error("Etwas ist schiefgelaufen. Bitte erneut versuchen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" data-testid="contact-section" className="relative py-24 md:py-32 bg-ink-900">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-xs tracking-[0.3em] uppercase text-gold mb-5">Kontakt</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-tight">
              Lassen Sie uns<br />
              <span className="italic text-gold-soft">sauber starten.</span>
            </h2>
            <p className="mt-6 text-zinc-400">
              Beschreiben Sie kurz Ihr Vorhaben — wir melden uns in der Regel innerhalb von
              24 Stunden mit einem Vorschlag oder einem Termin.
            </p>

            <div className="mt-10 space-y-5">
              <a data-testid="contact-info-phone" href={COMPANY.phoneHref} className="flex items-start gap-4 group">
                <div className="w-10 h-10 border border-gold/40 flex items-center justify-center group-hover:bg-gold/10 transition-colors">
                  <Phone size={16} className="text-gold" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Telefon</p>
                  <p className="text-white group-hover:text-gold transition-colors">{COMPANY.phone}</p>
                </div>
              </a>
              <a data-testid="contact-info-email" href={`mailto:${COMPANY.email}`} className="flex items-start gap-4 group">
                <div className="w-10 h-10 border border-gold/40 flex items-center justify-center group-hover:bg-gold/10 transition-colors">
                  <Mail size={16} className="text-gold" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">E-Mail</p>
                  <p className="text-white group-hover:text-gold transition-colors break-all">{COMPANY.email}</p>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 border border-gold/40 flex items-center justify-center">
                  <MapPin size={16} className="text-gold" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Adresse</p>
                  <p className="text-white">{COMPANY.address.street}</p>
                  <p className="text-white">{COMPANY.address.zip} {COMPANY.address.city}</p>
                </div>
              </div>
            </div>

            <MapsEmbed />
          </div>

          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="lg:col-span-7 bg-ink-700 border border-ink-500/60 p-8 md:p-10 space-y-5"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-gold mb-2 block">Name *</label>
                <Input
                  data-testid="contact-input-name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  required
                  className="bg-ink-900 border-ink-500 text-white h-12 focus-visible:ring-gold focus-visible:border-gold"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-gold mb-2 block">E-Mail *</label>
                <Input
                  data-testid="contact-input-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  required
                  className="bg-ink-900 border-ink-500 text-white h-12 focus-visible:ring-gold focus-visible:border-gold"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-gold mb-2 block">Telefon</label>
                <Input
                  data-testid="contact-input-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  className="bg-ink-900 border-ink-500 text-white h-12 focus-visible:ring-gold focus-visible:border-gold"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-gold mb-2 block">Leistung</label>
                <Select value={form.service} onValueChange={(v) => update("service", v)}>
                  <SelectTrigger data-testid="contact-select-service" className="bg-ink-900 border-ink-500 text-white h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-ink-700 border-ink-500 text-white">
                    {SERVICES.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`contact-service-${s}`}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-gold mb-2 block">Nachricht *</label>
              <Textarea
                data-testid="contact-input-message"
                rows={6}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                required
                className="bg-ink-900 border-ink-500 text-white focus-visible:ring-gold focus-visible:border-gold resize-none"
                placeholder="Objekt, Fläche, gewünschter Zeitraum…"
              />
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
              <p className="text-xs text-zinc-500">
                Mit dem Absenden akzeptieren Sie unsere <a href="/datenschutz" className="underline hover:text-gold">Datenschutzerklärung</a>.
              </p>
              <button
                data-testid="contact-submit"
                disabled={loading}
                type="submit"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-gold text-ink-900 text-sm font-medium uppercase tracking-wider hover:bg-gold-hover transition-colors disabled:opacity-60"
              >
                <Send size={16} />
                {loading ? "Sende…" : done ? "Erneut senden" : "Anfrage senden"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
